import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid2,
  CircularProgress,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BarChartIcon from '@mui/icons-material/BarChart';
import DiscussionsButton from '../utils/DiscussionsButton';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { getContests } from '../api/contests';

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

type Contest = {
  contest_id: number;
  name: string;
  description: string;
  intro_url?: string | null;
  total_entries: number;
};

const ContestList: React.FC = () => {
  const [contests, setContests] = useState<Contest[]>([]);
  const [chartOpen, setChartOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    getContests().then((data) => {
      // Sort contests by `total_entries` in descending order
      const sortedContests = data.sort((a: Contest, b: Contest) => b.total_entries - a.total_entries);
      setContests(sortedContests);
    });
  }, []);

  const handleChartOpen = () => setChartOpen(true);
  const handleChartClose = () => setChartOpen(false);

  // Adjust colors based on theme mode
  const isDarkMode = theme.palette.mode === 'dark';
  const chartColors = {
    background: isDarkMode ? '#4caf50' : '#1976d2',
    text: isDarkMode ? '#ffffff' : '#000000',
    grid: isDarkMode ? '#666666' : '#cccccc',
  };

  // Chart Data Preparation
  const chartData = {
    labels: contests.map((contest) => contest.name),
    datasets: [
      {
        label: 'Публикаций',
        data: contests.map((contest) => contest.total_entries),
        backgroundColor: chartColors.background,
      },
    ],
    total: contests.reduce((acc, contest) => acc + contest.total_entries, 0),
  };
  
  
  const desktopChartOptions: any = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw} публ.`,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Зарубы',
          color: chartColors.text,
        },
        ticks: { color: chartColors.text },
        grid: { color: chartColors.grid },
        beginAtZero: true,
      },
      y: {
        title: {
          display: true,
          text: 'Публикаций',
          color: chartColors.text,
        },
        ticks: { color: chartColors.text },
        grid: { color: chartColors.grid },
        beginAtZero: true,
      },
    },
    indexAxis: 'x', // Horizontal chart
  };

  const mobileChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false, // Important to allow fixed height
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw} публ.`,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Публикаций',
          color: chartColors.text,
        },
        ticks: { color: chartColors.text },
        grid: { color: chartColors.grid },
        beginAtZero: true,
      },
      y: {
        title: {
          display: true,
          color: chartColors.text,
        },
        ticks: { color: chartColors.text },
        grid: { color: chartColors.grid },
        beginAtZero: true,
      },
    },
    indexAxis: 'y', // Vertical chart
  };

  return (
      <>
        {/* Chart Icon */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <IconButton onClick={handleChartOpen} color="primary" title="График публикаций по зарубам">
            <BarChartIcon />
          </IconButton>
        </Box>

        {/* Contest List */}
        {!contests?.length ? (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
        ) : (
            <Grid2 container spacing={3} columns={12} justifyContent="flex-start">
              {contests.map((contest) => (
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={contest.contest_id}>
                    <Card
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: 3,
                        }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Typography
                              variant="h6"
                              component={Link}
                              to={`/contest/${contest.contest_id}`}
                              style={{
                                textDecoration: 'none',
                                color: '#1976d2',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                          >
                            {contest.name}
                            <ArrowForwardIcon
                                style={{
                                  marginLeft: '6px',
                                  verticalAlign: 'middle',
                                  fontSize: '1.1rem',
                                }}
                            />
                          </Typography>

                          <Chip
                              label={contest.total_entries.toString()}
                              variant="outlined"
                              color="primary"
                              sx={{ flexShrink: 0 }}
                          />
                        </Box>
                        <Box display={"flex"} justifyContent={"space-between"}>
                          <Link to={`/contest/${contest.contest_id}/?tab=0`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Box>лента</Box>
                          </Link>
                          <Link to={`/contest/${contest.contest_id}/?tab=1`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Box>рейтинг</Box>
                          </Link>
                          <Link to={`/contest/${contest.contest_id}/?tab=2`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Box>правила</Box>
                          </Link>
                        </Box>

                        <Typography
                            variant="body2"
                            color="textSecondary"
                            paragraph
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                            }}
                        >
                          {contest.description || 'No description available.'}
                        </Typography>

                        {contest.intro_url && (
                            <Box mt={1}>
                              <DiscussionsButton url={contest.intro_url} />
                            </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid2>
              ))}
            </Grid2>
        )}

        {/* Chart Popup */}
        <Dialog open={chartOpen} onClose={handleChartClose} fullWidth maxWidth="md">
          <DialogTitle>Количество публикаций в зарубах (всего {chartData.total})</DialogTitle>
          <DialogContent>
            {isMobile ? (
                <Box
                    sx={{
                      height: 500, // Fixed height for vertical chart
                      width: '100%', // Let it adjust width dynamically
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                >
                  <Bar data={chartData} options={mobileChartOptions} />
                </Box>
            ) : (
                <Box
                    sx={{
                      height: 400, // Fixed height for horizontal chart
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                >
                  <Bar data={chartData} options={desktopChartOptions} />
                </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
  );
};

export default ContestList;
